import { useState, useEffect, useCallback, useRef } from 'react';
import { X as CloseX, ChevronDown, ChevronRight, Zap, Save01, Edit01 } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { useTranslation } from 'react-i18next';
import { getAdminContainer, updateAdminContainer, analyzeContainer } from '@/data/api/admin-containers';
import type { AdminContainerDetail, AdminContainerEditData, VipField, AnalyzeResult } from '@/types/api/admin-containers';
import { useToast } from '@/hooks/use-toast';
import type { BadgeColors } from '@/components/base/badges/badge-types';
import { usePorts } from '@/hooks/api/use-ports';
import { useCompanies, useCreateCompany } from '@/hooks/api/use-companies';
import type { CompanyCreate } from '@/types/api/admin';

interface ContainerDetailDrawerProps {
    containerId: string;
    isOpen: boolean;
    onClose: () => void;
    onUpdated: () => void;
}

const STATUS_BADGE_COLORS: Record<string, BadgeColors> = {
    draft: 'gray', booked: 'blue', in_transit: 'brand', arrived: 'success',
    delivered: 'success', on_hold: 'warning', cancelled: 'error',
};

const VIP_STATUS_COLORS: Record<string, BadgeColors> = {
    missing: 'error', uncertain: 'warning', confirmed: 'success',
};

const DECISION_COLORS: Record<string, BadgeColors> = {
    CREATE: 'blue', LINK: 'success', REVIEW: 'warning', SKIP: 'gray',
};

// Map VIP field_name → editable container field(s)
// null = not directly editable (FK fields)
const VIP_EDIT_MAP: Record<string, string | string[] | null> = {
    container_number: 'container_number',
    carrier: 'carrier_name',
    origin_port: 'origin_port_id',
    destination_port: 'destination_port_id',
    consignee_name: 'consignee_company_id',
    etd: 'estimated_departure_date',
    eta: 'estimated_arrival_date',
    cargo_value: ['total_value_usd', 'currency'],
    cargo_description: 'cargo_description',
    cargo_details: ['total_weight_kg', 'total_volume_cbm', 'total_packages'],
    hs_code: 'hs_code_primary',
};

// Map VIP field_name → current display value from detail
function getVipDisplayValue(field: VipField, detail: AdminContainerDetail): string {
    const map: Record<string, () => string> = {
        container_number: () => detail.container_number || '',
        carrier: () => detail.carrier_name || '',
        origin_port: () => detail.origin_port_name || '',
        destination_port: () => detail.destination_port_name || '',
        consignee_name: () => detail.consignee_name || '',
        etd: () => detail.estimated_departure_date || '',
        eta: () => detail.estimated_arrival_date || '',
        cargo_value: () => detail.total_value_usd != null ? `${detail.total_value_usd} ${detail.currency}` : '',
        cargo_description: () => detail.cargo_description || '',
        cargo_details: () => {
            const parts = [];
            if (detail.total_weight_kg != null) parts.push(`${detail.total_weight_kg} kg`);
            if (detail.total_volume_cbm != null) parts.push(`${detail.total_volume_cbm} cbm`);
            if (detail.total_packages != null) parts.push(`${detail.total_packages} pkg`);
            return parts.join(' / ');
        },
        hs_code: () => detail.hs_code_primary || '',
    };
    return (map[field.field_name] || (() => ''))();
}

const MIN_WIDTH = 400;
const DEFAULT_WIDTH = 540;
const MAX_WIDTH = 800;

export const ContainerDetailDrawer = ({ containerId, isOpen, onClose, onUpdated }: ContainerDetailDrawerProps) => {
    const { t } = useTranslation(['pages:containers-admin', 'common']);
    const toast = useToast();

    const [detail, setDetail] = useState<AdminContainerDetail | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<AdminContainerEditData>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analyzeResult, setAnalyzeResult] = useState<AnalyzeResult | null>(null);
    const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const isDragging = useRef(false);

    useEffect(() => {
        if (!containerId || !isOpen) return;
        setIsLoadingDetail(true);
        setDetail(null);
        setAnalyzeResult(null);
        setIsEditing(false);

        getAdminContainer(containerId)
            .then(setDetail)
            .catch(() => toast.error(t('detail.saveError')))
            .finally(() => setIsLoadingDetail(false));
    }, [containerId, isOpen]);

    // Resize
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - e.clientX)));
        };
        const onUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        if (isOpen) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const startEditing = () => {
        if (!detail) return;
        setEditData({
            container_number: detail.container_number || '',
            booking_number: detail.booking_number || '',
            carrier_name: detail.carrier_name || '',
            vessel_name: detail.vessel_name || '',
            estimated_departure_date: detail.estimated_departure_date || '',
            estimated_arrival_date: detail.estimated_arrival_date || '',
            cargo_description: detail.cargo_description || '',
            hs_code_primary: detail.hs_code_primary || '',
            total_weight_kg: detail.total_weight_kg ?? undefined,
            total_volume_cbm: detail.total_volume_cbm ?? undefined,
            total_packages: detail.total_packages ?? undefined,
            total_value_usd: detail.total_value_usd ?? undefined,
            currency: detail.currency || 'USD',
        });
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!detail) return;
        setIsSaving(true);
        try {
            const payload: AdminContainerEditData = {};
            for (const [key, value] of Object.entries(editData)) {
                if (value !== undefined && value !== '') {
                    (payload as Record<string, unknown>)[key] = value;
                }
            }
            await updateAdminContainer(detail.container_id, payload);
            toast.success(t('detail.saved'));
            setIsEditing(false);
            const updated = await getAdminContainer(detail.container_id);
            setDetail(updated);
            onUpdated();
        } catch {
            toast.error(t('detail.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleAnalyze = async () => {
        if (!detail) return;
        setIsAnalyzing(true);
        setAnalyzeResult(null);
        try {
            const result = await analyzeContainer(detail.container_id);
            setAnalyzeResult(result);
        } catch {
            toast.error(t('detail.analyzeError'));
        } finally {
            setIsAnalyzing(false);
        }
    };

    const toggleField = (name: string) => {
        setExpandedFields((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    const getFieldLabel = (name: string): string => {
        const translated = t(`detail.fieldLabels.${name}`, { defaultValue: '' });
        if (translated && translated !== `detail.fieldLabels.${name}`) return translated;
        return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const formatDate = (s: string | null) => s ? new Date(s).toLocaleDateString('it-IT') : '-';

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-40 bg-overlay/50" onClick={onClose} />

            <div className="fixed inset-y-0 right-0 z-50 flex animate-in slide-in-from-right duration-200" style={{ width }}>
                <div onMouseDown={handleMouseDown} className="w-1.5 cursor-col-resize bg-transparent hover:bg-brand/30 active:bg-brand/50 transition-colors shrink-0" />

                <div className="flex flex-1 flex-col bg-primary ring-1 ring-secondary_alt shadow-xl overflow-hidden">
                    {/* Header */}
                    <header className="relative px-5 pt-4 pb-3 shrink-0 border-b border-secondary">
                        {isLoadingDetail ? (
                            <div className="animate-pulse h-6 w-48 rounded bg-secondary" />
                        ) : detail ? (
                            <div className="pr-20">
                                <h3 className="text-base font-semibold text-primary">
                                    {detail.container_number || detail.booking_number || t('detail.title')}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <Badge color={STATUS_BADGE_COLORS[detail.status.toLowerCase()] || 'gray'} size="sm">
                                        {detail.status.replace(/_/g, ' ')}
                                    </Badge>
                                    <span className="text-xs text-tertiary">{detail.company_name}</span>
                                    {detail.vip_completeness != null && (
                                        <span className="text-xs text-quaternary">VIP {Math.round(detail.vip_completeness * 100)}%</span>
                                    )}
                                </div>
                            </div>
                        ) : null}
                        <div className="absolute top-3 right-3 flex items-center gap-1.5">
                            {detail && (
                                <Button size="sm" color="secondary" iconLeading={Zap} isLoading={isAnalyzing} isDisabled={isAnalyzing} onClick={handleAnalyze}>
                                    {isAnalyzing ? '...' : 'Analyze'}
                                </Button>
                            )}
                            <button type="button" onClick={onClose} className="rounded-lg p-1 text-tertiary hover:text-primary hover:bg-secondary transition-colors">
                                <CloseX className="size-5" />
                            </button>
                        </div>
                    </header>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-5 py-4">
                        {isLoadingDetail ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
                            </div>
                        ) : detail ? (
                            <div className="space-y-4">
                                {/* Quick info row */}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    <InfoLine label="Booking" value={detail.booking_number || '-'} />
                                    <InfoLine label="Vessel" value={detail.vessel_name || '-'} />
                                    <InfoLine label="Route" value={
                                        detail.origin_port_name && detail.destination_port_name
                                            ? `${detail.origin_port_name} → ${detail.destination_port_name}`
                                            : '-'
                                    } />
                                    <InfoLine label="Consignee" value={detail.consignee_name || '-'} />
                                </div>

                                {/* VIP Fields — unified view with inline edit */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-primary">{t('detail.vipFields')}</span>
                                        {!isEditing ? (
                                            <Button size="sm" color="secondary" iconLeading={Edit01} onClick={startEditing}>
                                                {t('detail.editFields')}
                                            </Button>
                                        ) : (
                                            <div className="flex gap-1.5">
                                                <Button size="sm" color="secondary" onClick={() => setIsEditing(false)}>
                                                    Cancel
                                                </Button>
                                                <Button size="sm" color="primary" iconLeading={Save01} isLoading={isSaving} onClick={handleSave}>
                                                    {isSaving ? t('detail.saving') : t('detail.save')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        {(detail.vip_fields || []).map((field) => {
                                            const editKey = VIP_EDIT_MAP[field.field_name];
                                            const isEditable = editKey !== null && editKey !== undefined;
                                            const isExpanded = expandedFields.has(field.field_name);
                                            const displayValue = getVipDisplayValue(field, detail);

                                            return (
                                                <div key={field.field_name} className="rounded-lg border border-secondary">
                                                    {/* Row header */}
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleField(field.field_name)}
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary/50 transition-colors rounded-lg"
                                                    >
                                                        {isExpanded
                                                            ? <ChevronDown className="size-3 text-tertiary shrink-0" />
                                                            : <ChevronRight className="size-3 text-tertiary shrink-0" />
                                                        }
                                                        <span className="text-xs font-medium text-primary min-w-[90px] shrink-0">
                                                            {getFieldLabel(field.field_name)}
                                                        </span>
                                                        <Badge color={VIP_STATUS_COLORS[field.status] || 'gray'} size="sm">
                                                            {t(`detail.status.${field.status}`, { defaultValue: field.status })}
                                                        </Badge>
                                                        <span className="flex-1 text-xs text-tertiary truncate text-right">
                                                            {displayValue || '-'}
                                                        </span>
                                                        <span className="text-[10px] text-quaternary shrink-0 ml-1">
                                                            {field.source_count}s {Math.round(field.confidence * 100)}%
                                                        </span>
                                                    </button>

                                                    {/* Expanded: sources + inline edit */}
                                                    {isExpanded && (
                                                        <div className="px-3 pb-3 border-t border-secondary space-y-2 mt-0">
                                                            {/* Inline edit */}
                                                            {isEditing && isEditable && (
                                                                <div className="pt-2">
                                                                    <VipInlineEdit
                                                                        fieldName={field.field_name}
                                                                        editKey={editKey}
                                                                        editData={editData}
                                                                        setEditData={setEditData}
                                                                    />
                                                                </div>
                                                            )}

                                                            {/* Sources */}
                                                            <div className="pt-1">
                                                                <div className="flex items-center justify-between text-[10px] text-quaternary mb-1">
                                                                    <span>{t('detail.sources')} ({field.source_count})</span>
                                                                    {field.concordance && <Badge color="success" size="sm">OK</Badge>}
                                                                </div>
                                                                {field.sources.length > 0 ? (
                                                                    <div className="space-y-1">
                                                                        {field.sources.map((src, i) => (
                                                                            <div key={`${src.source_type}-${i}`} className="rounded bg-secondary px-2 py-1.5 text-[11px]">
                                                                                <div className="flex items-center justify-between">
                                                                                    <Badge color="gray" size="sm">{src.source_type}</Badge>
                                                                                    {src.extracted_at && (
                                                                                        <span className="text-quaternary">{formatDate(src.extracted_at)}</span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="mt-0.5 text-primary font-mono break-all">{src.value}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-[10px] text-quaternary italic">{t('detail.noSources')}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Agent analysis result (shown after clicking Analyze in header) */}
                                {analyzeResult && (
                                    <div className="rounded-lg border border-secondary p-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-primary">{t('detail.agentResult')}</span>
                                            <Badge color={DECISION_COLORS[analyzeResult.decision] || 'gray'} size="sm">
                                                {t(`detail.decision.${analyzeResult.decision}`, { defaultValue: analyzeResult.decision })}
                                            </Badge>
                                        </div>
                                        <div className="text-xs text-tertiary">{t('detail.confidence')}: {Math.round(analyzeResult.confidence * 100)}%</div>
                                        <p className="text-xs text-primary whitespace-pre-wrap">{analyzeResult.reasoning}</p>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

// --- Sub-components ---

const InfoLine = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-baseline gap-1 py-0.5">
        <span className="text-quaternary shrink-0">{label}:</span>
        <span className="text-primary truncate">{value}</span>
    </div>
);

interface VipInlineEditProps {
    fieldName: string;
    editKey: string | string[];
    editData: AdminContainerEditData;
    setEditData: React.Dispatch<React.SetStateAction<AdminContainerEditData>>;
}

const VipInlineEdit = ({ fieldName, editKey, editData, setEditData }: VipInlineEditProps) => {
    const update = (key: string, value: string) => {
        setEditData((d) => ({ ...d, [key]: value }));
    };
    const updateNum = (key: string, value: string) => {
        setEditData((d) => ({ ...d, [key]: value ? Number(value) : undefined }));
    };

    // Port autocomplete
    if (fieldName === 'origin_port' || fieldName === 'destination_port') {
        return <PortAutocomplete editKey={editKey as string} editData={editData} setEditData={setEditData} />;
    }

    // Company select for consignee
    if (fieldName === 'consignee_name') {
        return <CompanySelect editKey={editKey as string} editData={editData} setEditData={setEditData} />;
    }

    if (fieldName === 'cargo_value') {
        return (
            <div className="grid grid-cols-2 gap-2">
                <Input size="sm" label="Value" value={editData.total_value_usd?.toString() || ''} onChange={(v) => updateNum('total_value_usd', v)} />
                <Input size="sm" label="Currency" value={editData.currency || ''} onChange={(v) => update('currency', v)} />
            </div>
        );
    }

    if (fieldName === 'cargo_details') {
        return (
            <div className="grid grid-cols-3 gap-2">
                <Input size="sm" label="kg" value={editData.total_weight_kg?.toString() || ''} onChange={(v) => updateNum('total_weight_kg', v)} />
                <Input size="sm" label="cbm" value={editData.total_volume_cbm?.toString() || ''} onChange={(v) => updateNum('total_volume_cbm', v)} />
                <Input size="sm" label="pkg" value={editData.total_packages?.toString() || ''} onChange={(v) => updateNum('total_packages', v)} />
            </div>
        );
    }

    // Single field
    const key = editKey as string;
    const isDate = fieldName === 'etd' || fieldName === 'eta';
    return (
        <Input
            size="sm"
            value={(editData as Record<string, unknown>)[key]?.toString() || ''}
            onChange={(v) => update(key, v)}
            placeholder={isDate ? 'YYYY-MM-DD' : undefined}
        />
    );
};

// --- Port Autocomplete ---

interface PortAutoProps {
    editKey: string;
    editData: AdminContainerEditData;
    setEditData: React.Dispatch<React.SetStateAction<AdminContainerEditData>>;
}

const PortAutocomplete = ({ editKey, editData: _editData, setEditData }: PortAutoProps) => {
    const [search, setSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedName, setSelectedName] = useState('');
    const { data } = usePorts(search, undefined, search.length >= 2);

    const ports = data?.ports || [];

    return (
        <div className="relative">
            <Input
                size="sm"
                placeholder="Cerca porto..."
                value={selectedName || search}
                onChange={(v) => {
                    setSearch(v);
                    setSelectedName('');
                    setShowDropdown(true);
                    if (!v) {
                        setEditData((d) => ({ ...d, [editKey]: undefined }));
                    }
                }}
                onFocus={() => search.length >= 2 && setShowDropdown(true)}
            />
            {showDropdown && ports.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto rounded-lg bg-primary ring-1 ring-secondary shadow-lg">
                    {ports.map((p) => (
                        <button
                            key={p.port_id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-xs hover:bg-secondary transition-colors"
                            onClick={() => {
                                setEditData((d) => ({ ...d, [editKey]: p.port_id }));
                                setSelectedName(p.display_name);
                                setSearch('');
                                setShowDropdown(false);
                            }}
                        >
                            <span className="text-primary">{p.port_name}</span>
                            <span className="text-quaternary ml-1">{p.country_code} ({p.un_locode})</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- Company Select for Consignee ---

interface CompanySelectProps {
    editKey: string;
    editData: AdminContainerEditData;
    setEditData: React.Dispatch<React.SetStateAction<AdminContainerEditData>>;
}

const CompanySelect = ({ editKey, editData: _editData, setEditData }: CompanySelectProps) => {
    const [search, setSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedName, setSelectedName] = useState('');
    const [showAddNew, setShowAddNew] = useState(false);
    const [newCompanyName, setNewCompanyName] = useState('');
    const { data: allCompanies = [] } = useCompanies({ limit: 200 });
    const companies = search
        ? allCompanies.filter((c) => c.company_name.toLowerCase().includes(search.toLowerCase())).slice(0, 20)
        : allCompanies.slice(0, 20);
    const createCompany = useCreateCompany();

    const handleAddNew = async () => {
        if (!newCompanyName.trim()) return;
        try {
            const created = await createCompany.mutateAsync({
                company_name: newCompanyName.trim(),
                company_type: 'receiver',
            } as CompanyCreate);
            setEditData((d) => ({ ...d, [editKey]: created.company_id }));
            setSelectedName(created.company_name);
            setShowAddNew(false);
            setNewCompanyName('');
            setShowDropdown(false);
        } catch {
            // error handled by mutation
        }
    };

    return (
        <div className="relative">
            {!showAddNew ? (
                <>
                    <div className="flex gap-1.5">
                        <div className="flex-1 relative">
                            <Input
                                size="sm"
                                placeholder="Cerca company..."
                                value={selectedName || search}
                                onChange={(v) => {
                                    setSearch(v);
                                    setSelectedName('');
                                    setShowDropdown(true);
                                    if (!v) {
                                        setEditData((d) => ({ ...d, [editKey]: undefined }));
                                    }
                                }}
                                onFocus={() => setShowDropdown(true)}
                            />
                            {showDropdown && companies.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto rounded-lg bg-primary ring-1 ring-secondary shadow-lg">
                                    {companies.map((c) => (
                                        <button
                                            key={c.company_id}
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-secondary transition-colors"
                                            onClick={() => {
                                                setEditData((d) => ({ ...d, [editKey]: c.company_id }));
                                                setSelectedName(c.company_name);
                                                setSearch('');
                                                setShowDropdown(false);
                                            }}
                                        >
                                            <span className="text-primary">{c.company_name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button size="sm" color="secondary" onClick={() => { setShowAddNew(true); setShowDropdown(false); }}>
                            + New
                        </Button>
                    </div>
                </>
            ) : (
                <div className="flex gap-1.5">
                    <div className="flex-1">
                        <Input
                            size="sm"
                            placeholder="Nome nuova company..."
                            value={newCompanyName}
                            onChange={setNewCompanyName}
                        />
                    </div>
                    <Button size="sm" color="primary" isLoading={createCompany.isPending} onClick={handleAddNew}>
                        Crea
                    </Button>
                    <Button size="sm" color="secondary" onClick={() => { setShowAddNew(false); setNewCompanyName(''); }}>
                        X
                    </Button>
                </div>
            )}
        </div>
    );
};
